import { useRef, useState } from 'react';
import { crm } from '@buildbee/domain';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  Status,
  Textarea,
  segmentedItemClass,
  segmentedListClass,
  useToast,
} from '@buildbee/ui';
import { AddressFields, addressErrors, cleanAddress, type AddressValue } from '../../lib/address';
import { errorMessage, trpc, type RouterOutputs } from '../../lib/trpc';
import { CompanyHeader } from './nav';

type SupplierType = 'supplier' | 'subcontractor';
type Preview = RouterOutputs['platform']['suppliers']['importPreview'];

const PREVIEW_ROWS = 50;
const CHUNK = 25;
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const MAX_ROWS = 5000;

const split = (v: string) =>
  v
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean);

function TypePicker({ value, onChange }: { value: SupplierType; onChange: (v: SupplierType) => void }) {
  return (
    <div role="group" aria-label="Type" className={segmentedListClass}>
      {(['subcontractor', 'supplier'] as const).map((t) => (
        <button key={t} type="button" aria-pressed={value === t} className={segmentedItemClass(value === t)} onClick={() => onChange(t)}>
          {t === 'subcontractor' ? 'Subcontractor' : 'Supplier'}
        </button>
      ))}
    </div>
  );
}

/** /admin/workspaces/$workspaceId/suppliers: add subs and suppliers to a company's CRM. */
export function PlatformCompanySuppliersPage({ workspaceId }: { workspaceId: string }) {
  const company = trpc.platform.members.list.useQuery({ workspaceId });
  if (company.isLoading) return <LoadingState />;
  if (company.error || !company.data) return <ErrorState error={company.error} onRetry={() => void company.refetch()} />;
  return (
    <div className="space-y-6">
      <CompanyHeader workspaceId={workspaceId} current="suppliers" name={company.data.workspace.name} />
      <p className="text-sm text-muted-foreground">
        Suppliers and subcontractors go into the company&apos;s CRM (Subs &amp; vendors) with a vendor profile. Nobody at the company owns them until someone picks them up.
      </p>
      <div className="grid gap-6 xl:grid-cols-2">
        <AddSupplierCard workspaceId={workspaceId} />
        <ImportCard workspaceId={workspaceId} />
      </div>
    </div>
  );
}

function AddSupplierCard({ workspaceId }: { workspaceId: string }) {
  const toast = useToast();
  const utils = trpc.useUtils();
  const empty = { name: '', trades: '', phone: '', email: '', website: '', licenseNumber: '', insuranceExpiresAt: '', notes: '', contactFirst: '', contactLast: '', contactEmail: '', contactPhone: '', contactTitle: '' };
  const [type, setType] = useState<SupplierType>('subcontractor');
  const [f, setF] = useState(empty);
  const [address, setAddress] = useState<AddressValue>({});
  const [duplicate, setDuplicate] = useState<{ name: string } | null>(null);
  const set = (patch: Partial<typeof empty>) => setF((v) => ({ ...v, ...patch }));
  const create = trpc.platform.suppliers.create.useMutation({
    onSuccess: (out) => {
      if (!out.created) {
        setDuplicate(out.duplicates[0] ?? { name: f.name });
        return;
      }
      toast.success(`${f.name} added`, f.contactFirst || f.contactEmail ? 'With its contact person.' : undefined);
      setF(empty);
      setAddress({});
      setDuplicate(null);
      void utils.platform.audit.invalidate();
    },
    onError: (err) => toast.error("Couldn't add the supplier", errorMessage(err)),
  });
  const errors = addressErrors(create.error);
  const submit = (allowDuplicate = false) => {
    const wantsContact = f.contactFirst || f.contactLast || f.contactEmail || f.contactPhone;
    create.mutate({
      workspaceId,
      relationshipType: type,
      name: f.name.trim(),
      trades: split(f.trades),
      phone: f.phone.trim() || null,
      email: f.email.trim() || null,
      website: f.website.trim() ? (/^https?:\/\//i.test(f.website.trim()) ? f.website.trim() : `https://${f.website.trim()}`) : null,
      licenseNumber: f.licenseNumber.trim() || null,
      insuranceExpiresAt: f.insuranceExpiresAt || null,
      notes: f.notes.trim() || null,
      address: cleanAddress(address),
      contact: wantsContact
        ? { firstName: f.contactFirst.trim(), lastName: f.contactLast.trim(), email: f.contactEmail.trim() || null, phone: f.contactPhone.trim() || null, jobTitle: f.contactTitle.trim() || null }
        : undefined,
      allowDuplicate,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add one</CardTitle>
        <CardDescription>A single subcontractor or supplier, with an optional contact person.</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <TypePicker value={type} onChange={setType} />
          <Field label="Company name" htmlFor="sp-name" required>
            <Input id="sp-name" value={f.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="Trades" htmlFor="sp-trades" description="Separate with commas, e.g. Electrical, Low voltage.">
            <Input id="sp-trades" value={f.trades} onChange={(e) => set({ trades: e.target.value })} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Phone" htmlFor="sp-phone">
              <Input id="sp-phone" type="tel" value={f.phone} onChange={(e) => set({ phone: e.target.value })} />
            </Field>
            <Field label="Email" htmlFor="sp-email">
              <Input id="sp-email" type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} />
            </Field>
            <Field label="Website" htmlFor="sp-web">
              <Input id="sp-web" value={f.website} onChange={(e) => set({ website: e.target.value })} placeholder="example.ca" />
            </Field>
            <Field label="Licence number" htmlFor="sp-lic">
              <Input id="sp-lic" value={f.licenseNumber} onChange={(e) => set({ licenseNumber: e.target.value })} />
            </Field>
            <Field label="Insurance expires" htmlFor="sp-ins">
              <Input id="sp-ins" type="date" className="figures" value={f.insuranceExpiresAt} onChange={(e) => set({ insuranceExpiresAt: e.target.value })} />
            </Field>
          </div>
          <AddressFields value={address} onChange={setAddress} errors={errors} />
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">Contact person (optional)</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="First name" htmlFor="sp-cf">
                <Input id="sp-cf" value={f.contactFirst} onChange={(e) => set({ contactFirst: e.target.value })} />
              </Field>
              <Field label="Last name" htmlFor="sp-cl">
                <Input id="sp-cl" value={f.contactLast} onChange={(e) => set({ contactLast: e.target.value })} />
              </Field>
              <Field label="Email" htmlFor="sp-ce">
                <Input id="sp-ce" type="email" value={f.contactEmail} onChange={(e) => set({ contactEmail: e.target.value })} />
              </Field>
              <Field label="Phone" htmlFor="sp-cp">
                <Input id="sp-cp" type="tel" value={f.contactPhone} onChange={(e) => set({ contactPhone: e.target.value })} />
              </Field>
              <Field label="Title" htmlFor="sp-ct">
                <Input id="sp-ct" value={f.contactTitle} onChange={(e) => set({ contactTitle: e.target.value })} placeholder="Estimator" />
              </Field>
            </div>
          </fieldset>
          <Field label="Notes" htmlFor="sp-notes">
            <Textarea id="sp-notes" rows={2} value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
          </Field>
          <Button type="submit" disabled={!f.name.trim()} loading={create.isPending}>
            Add {type === 'subcontractor' ? 'subcontractor' : 'supplier'}
          </Button>
        </form>
      </CardContent>
      <ConfirmDialog
        open={duplicate !== null}
        onOpenChange={(o) => !o && setDuplicate(null)}
        title="This company may already be in their CRM"
        description={`It looks like “${duplicate?.name ?? ''}”, which they already have. Add it anyway?`}
        confirmLabel="Add anyway"
        loading={create.isPending}
        onConfirm={() => submit(true)}
      />
    </Card>
  );
}

type Parsed = { fileName: string; headers: string[]; rows: string[][] };
type Progress = { done: number; total: number; created: number; contacts: number; skipped: { row: number; name: string; matches: string }[]; failed: { row: number; errors: string[] }[]; finished: boolean; error: string | null };

function ImportCard({ workspaceId }: { workspaceId: string }) {
  const utils = trpc.useUtils();
  const fileRef = useRef<HTMLInputElement>(null);
  const [type, setType] = useState<SupplierType>('subcontractor');
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, string> | undefined>(undefined);
  const [onDuplicate, setOnDuplicate] = useState<'skip' | 'create'>('skip');
  const [progress, setProgress] = useState<Progress | null>(null);
  const [confirming, setConfirming] = useState(false);
  const fields = trpc.platform.suppliers.fields.useQuery();
  const preview = trpc.platform.suppliers.importPreview.useQuery(
    { workspaceId, headers: parsed?.headers ?? ['x'], rows: parsed?.rows.slice(0, PREVIEW_ROWS) ?? [], mapping, relationshipType: type },
    { enabled: !!parsed && !progress },
  );
  const importChunk = trpc.platform.suppliers.import.useMutation();

  const reset = () => {
    setParsed(null);
    setMapping(undefined);
    setProgress(null);
    setFileError(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const onFile = async (file: File | undefined) => {
    reset();
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) return setFileError('That file is over 2 MB. Split it into smaller files.');
    const table = crm.parseCsv(await file.text()).filter((r) => r.some((c) => c.trim()));
    if (table.length < 2) return setFileError('The file needs a header row and at least one supplier.');
    if (table.length - 1 > MAX_ROWS) return setFileError(`The file has ${table.length - 1} rows. Import at most ${MAX_ROWS} at a time.`);
    setParsed({ fileName: file.name, headers: table[0]!.map((h) => h.trim()), rows: table.slice(1) });
  };

  const effectiveMapping = mapping ?? preview.data?.mapping ?? {};

  const run = async () => {
    if (!parsed) return;
    setConfirming(false);
    const importId = crypto.randomUUID();
    const total = parsed.rows.length;
    let state: Progress = { done: 0, total, created: 0, contacts: 0, skipped: [], failed: [], finished: false, error: null };
    setProgress(state);
    for (let start = 0; start < total; start += CHUNK) {
      try {
        const out = await importChunk.mutateAsync({
          workspaceId,
          headers: parsed.headers,
          rows: parsed.rows.slice(start, start + CHUNK),
          mapping: effectiveMapping,
          relationshipType: type,
          onDuplicate,
          importId,
          startRow: start,
        });
        state = { ...state, done: Math.min(total, start + CHUNK), created: state.created + out.created, contacts: state.contacts + out.contacts, skipped: [...state.skipped, ...out.skipped], failed: [...state.failed, ...out.failed] };
        setProgress(state);
      } catch (err) {
        state = { ...state, finished: true, error: `Stopped at row ${start + 2}: ${errorMessage(err)}. Rows before it were imported; fix the file and import the rest.` };
        setProgress(state);
        void utils.platform.audit.invalidate();
        return;
      }
    }
    setProgress({ ...state, finished: true });
    void utils.platform.audit.invalidate();
  };

  const rows = preview.data?.rows ?? [];
  const bad = rows.filter((r) => !r.ok).length;
  const dupes = rows.filter((r) => r.ok && (r.duplicate || r.duplicateInFile !== null)).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Import a CSV file</CardTitle>
        <CardDescription>One row per company. Columns such as Company, Trade, Phone, Email, Website, Province, Postal code, Contact and Contact email are matched for you.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!progress && (
          <>
            <TypePicker value={type} onChange={setType} />
            <Field label="CSV file" htmlFor="imp-file" error={fileError}>
              <Input id="imp-file" ref={fileRef} type="file" accept=".csv,text/csv" className="cursor-pointer py-1.5 text-sm text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-md file:border file:border-border file:bg-card file:px-3 file:py-1 file:text-[13px] file:font-medium file:text-foreground hover:file:bg-foreground/[0.04]" onChange={(e) => void onFile(e.target.files?.[0])} />
            </Field>
          </>
        )}

        {parsed && !progress && (
          <>
            <p className="text-sm">
              <span className="font-medium">{parsed.fileName}</span> · <span className="figures">{parsed.rows.length}</span> rows
            </p>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Columns</legend>
              {parsed.headers.map((h) => (
                <div key={h} className="grid grid-cols-2 items-center gap-2 text-sm">
                  <span className="truncate">{h || <span className="text-muted-foreground">(no header)</span>}</span>
                  <Select
                    aria-label={`Column ${h}`}
                    value={effectiveMapping[h] ?? ''}
                    options={[{ value: '', label: 'Skip this column' }, ...(fields.data ?? []).map((x) => ({ value: x.key, label: x.label }))]}
                    onChange={(e) => {
                      const next = { ...effectiveMapping };
                      if (e.target.value) next[h] = e.target.value;
                      else delete next[h];
                      setMapping(next);
                    }}
                  />
                </div>
              ))}
            </fieldset>

            {preview.isLoading ? (
              <LoadingState label="Checking the rows" />
            ) : preview.error ? (
              <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
            ) : (
              <PreviewTable rows={rows} sampled={parsed.rows.length > PREVIEW_ROWS} />
            )}

            <Field label="Companies they already have" htmlFor="imp-dupes">
              <Select
                id="imp-dupes"
                value={onDuplicate}
                options={[
                  { value: 'skip', label: 'Skip them (recommended)' },
                  { value: 'create', label: 'Add them again anyway' },
                ]}
                onChange={(e) => setOnDuplicate(e.target.value as 'skip' | 'create')}
              />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button disabled={!preview.data || !Object.values(effectiveMapping).includes('name')} onClick={() => setConfirming(true)}>
                Import {parsed.rows.length} rows
              </Button>
              <Button variant="ghost" onClick={reset}>
                Start over
              </Button>
            </div>
            {!Object.values(effectiveMapping).includes('name') && <Alert variant="warning">Pick which column holds the company name.</Alert>}
          </>
        )}

        {progress && <ImportProgress progress={progress} onDone={reset} />}
      </CardContent>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Import ${parsed?.rows.length ?? 0} ${type === 'subcontractor' ? 'subcontractors' : 'suppliers'}?`}
        description={`${bad ? `${bad} of the first ${rows.length} rows have errors and will be left out. ` : ''}${dupes && onDuplicate === 'skip' ? `${dupes} look like companies they already have and will be skipped. ` : ''}Rows are added in batches of ${CHUNK}; you can't undo an import here.`}
        confirmLabel="Import"
        onConfirm={() => void run()}
      />
    </Card>
  );
}

function PreviewTable({ rows, sampled }: { rows: Preview['rows']; sampled: boolean }) {
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{sampled ? `First ${rows.length} rows:` : 'Preview:'}</p>
      <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-lg border border-border text-sm">
        {rows.map((r) => (
          <li key={r.index} className="flex flex-wrap items-start justify-between gap-2 px-3 py-2">
            <span>
              <span className="figures mr-2 text-muted-foreground">{r.index + 2}</span>
              {r.name ?? <span className="text-muted-foreground">No company name</span>}
              {r.contactName && <span className="text-muted-foreground"> · {r.contactName}</span>}
              {!r.ok && <span className="block text-xs text-destructive">{r.errors.join('; ')}</span>}
            </span>
            {!r.ok ? (
              <Status tone="blocked">Error</Status>
            ) : r.duplicate ? (
              <Status tone="risk">{`Looks like ${r.duplicate.name}`}</Status>
            ) : r.duplicateInFile !== null ? (
              <Status tone="risk">{`Same as row ${r.duplicateInFile + 2}`}</Status>
            ) : (
              <Status tone="done">Ready</Status>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ImportProgress({ progress, onDone }: { progress: Progress; onDone: () => void }) {
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  return (
    <div className="space-y-3" aria-live="polite">
      <div className="h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-sm">
        <span className="figures">{progress.done}</span> of <span className="figures">{progress.total}</span> rows · <span className="figures">{progress.created}</span> added
        {progress.contacts ? (
          <>
            {' '}
            (<span className="figures">{progress.contacts}</span> contacts)
          </>
        ) : null}
        {progress.skipped.length ? (
          <>
            {' '}
            · <span className="figures">{progress.skipped.length}</span> skipped
          </>
        ) : null}
        {progress.failed.length ? (
          <>
            {' '}
            · <span className="figures">{progress.failed.length}</span> with errors
          </>
        ) : null}
      </p>
      {progress.error && <Alert variant="destructive">{progress.error}</Alert>}
      {progress.finished && !progress.error && <Alert variant="success">Import finished.</Alert>}
      {progress.finished && (progress.failed.length > 0 || progress.skipped.length > 0) && (
        <ul className="max-h-60 space-y-1 overflow-y-auto text-sm">
          {progress.failed.map((f) => (
            <li key={`f${f.row}`}>
              <span className="figures">Row {f.row + 2}</span>: {f.errors.join('; ')}
            </li>
          ))}
          {progress.skipped.map((s) => (
            <li key={`s${s.row}`} className="text-muted-foreground">
              <span className="figures">Row {s.row + 2}</span>: {s.name} skipped, matches {s.matches}
            </li>
          ))}
        </ul>
      )}
      {progress.finished && (
        <Button variant="outline" onClick={onDone}>
          Import another file
        </Button>
      )}
    </div>
  );
}
